import { PauseCover } from "aic";
import { RouteObject } from "react-router-dom";
import AppSettingsContextProvider from "./contexts/AppSettingsContextProvider";
import { hierarchyLoader } from "./functions/api/hierarchy/hierarchyLoader";
import { hierarchyRedirectLoader } from "./functions/api/hierarchy/hierarchyRedirectLoader";
import { newTlfLoader } from "./functions/api/tlfItem/newTlfLoader";
import { tlfLoader, tlfOnlyLoader } from "./functions/api/tlfItem/tlfLoader";
import { tlfRedirectLoader } from "./functions/api/tlfItem/tlfRedirectLoader";
import { appSettingsLoader } from "./functions/api/user/appSettingsLoader";
import RedirectingPage from "./pages/RedirectingPage";
import RouterErrorBoundary from "./pages/RouterErrorBoundary";
import StartPage from "./pages/StartPage";
import TlfReview, { TlfReviewRHS } from "./pages/TlfReview";

export const routes: RouteObject[] = [
  {
    path: "/",
    loader: appSettingsLoader,
    errorElement: <RouterErrorBoundary />,
    element: <AppSettingsContextProvider />,
    hydrateFallbackElement: <PauseCover text="Loading application..." />,
    children: [
      {
        index: true,
        element: <StartPage />,
      },
      // Admin page
      {
        path: "/Admin",
        lazy: async () => ({
          Component: (await import("./pages/AdminPage")).AdminPage,
        }),
      },
      // Maintenance page
      {
        path: "/Maintenance",
        lazy: async () => ({
          Component: (await import("./pages/MaintenancePage")).MaintenancePage,
        }),
      },
      // Not Allowed Page
      {
        path: "/NotAllowed",
        lazy: async () => ({
          Component: (await import("./pages/NotAllowedPage")).NotAllowedPage,
        }),
      },

      // New hierarchy pages
      ...[
        "/Corporate",
        "/TA/:hierarchyTag",
        "/Project/:hierarchyTag",
        "/Study/:hierarchyTag",
        "/Study/:studyName/Report/:reportName",
        "/Study/:studyName/Report/:reportName/Version/:tocMajorVersion",
      ].map((path) => ({
        path,
        loader: hierarchyLoader,
        lazy: async () => ({
          Component: (await import("./pages/HierarchyPage")).HierarchyPage,
        }),
        children: [
          {
            index: true,
            lazy: async () => ({
              Component: path.includes("Report")
                ? (await import("./components/toc/TOC")).TOC
                : (await import("./components/hierarchypage/HierarchyProperties"))
                    .HierarchyProperties,
            }),
          },
          {
            path: "Access",
            lazy: async () => ({
              Component: (await import("./components/access/AccessRights")).AccessRights,
            }),
          },
          {
            path: "ClinicalLibrary",
            children: [
              {
                index: true,
                lazy: async () => ({
                  Component: (await import("./components/library/LibraryNavigation"))
                    .LibraryNavigation,
                }),
              },
              {
                path: "CodeLists",
                lazy: async () => ({
                  Component: (await import("./components/library/LibraryTable")).LibraryTable,
                }),
              },
              {
                path: "CodeList/:codeListName",
                lazy: async () => ({
                  Component: (await import("./components/library/LibraryTable")).LibraryTable,
                }),
              },
              {
                path: "DataSets",
                lazy: async () => ({
                  Component: (await import("./components/library/LibraryTable")).LibraryTable,
                }),
              },
              {
                path: "DataSet/:dataSetName",
                lazy: async () => ({
                  Component: (await import("./components/library/LibraryTable")).LibraryTable,
                }),
              },
              {
                path: "ValueLists",
                lazy: async () => ({
                  Component: (await import("./components/library/LibraryTable")).LibraryTable,
                }),
              },
              {
                path: "ValueList/:valueListName",
                lazy: async () => ({
                  Component: (await import("./components/library/LibraryTable")).LibraryTable,
                }),
              },
              {
                path: "Comments",
                lazy: async () => ({
                  Component: (await import("./components/library/LibraryTable")).LibraryTable,
                }),
              },
              {
                path: "Methods",
                lazy: async () => ({
                  Component: (await import("./components/library/LibraryTable")).LibraryTable,
                }),
              },
              {
                path: "WhereClauses",
                lazy: async () => ({
                  Component: (await import("./components/library/LibraryTable")).LibraryTable,
                }),
              },
              {
                path: "WhereClause/:whereClauseName/RangeCheck/:rangeCheckId",
                lazy: async () => ({
                  Component: (await import("./components/library/LibraryTable")).LibraryTable,
                }),
              },
              {
                path: "WhereClause/:whereClauseName",
                lazy: async () => ({
                  Component: (await import("./components/library/LibraryTable")).LibraryTable,
                }),
              },
              {
                path: "Comment/:commentId",
                lazy: async () => ({
                  Component: (await import("./components/library/LibraryTable")).LibraryTable,
                }),
              },
              {
                path: "Method/:methodOid",
                lazy: async () => ({
                  Component: (await import("./components/library/LibraryTable")).LibraryTable,
                }),
              },
            ],
          },
          {
            path: "DomainReplacements",
            lazy: async () => ({
              Component: (await import("./components/inherited/HierarchyDomainReplacements"))
                .HierarchyDomainReplacements,
            }),
          },
          {
            path: "Lists",
            lazy: async () => ({
              Component: (await import("./components/inherited/HierarchyLists")).HierarchyLists,
            }),
          },
          {
            path: "Properties",
            lazy: async () => ({
              Component: (await import("./components/hierarchypage/HierarchyProperties"))
                .HierarchyProperties,
            }),
          },
          {
            path: "Replacements",
            lazy: async () => ({
              Component: (await import("./components/inherited/ExternalSinglesTable"))
                .ExternalSinglesTable,
            }),
          },
          {
            path: "RowGroups",
            lazy: async () => ({
              Component: (await import("./components/inherited/HierarchyRowGroups"))
                .HierarchyRowGroups,
            }),
          },
          {
            path: "AnalysisSets",
            lazy: async () => ({
              Component: (await import("./components/ars/HierarchyAnalysisSets"))
                .HierarchyAnalysisSets,
            }),
          },
          {
            path: "DataSubsets",
            lazy: async () => ({
              Component: (await import("./components/ars/HierarchyDataSubsets"))
                .HierarchyDataSubsets,
            }),
          },
          {
            path: "GroupingFactors",
            lazy: async () => ({
              Component: (await import("./components/ars/HierarchyGroupingFactors"))
                .HierarchyGroupingFactors,
            }),
          },
          {
            path: "TOC",
            lazy: async () => ({
              Component: path.includes("Report")
                ? (await import("./components/toc/TOC")).TOC
                : (await import("./pages/NotFound")).NotFound,
            }),
          },
          {
            path: "Versions",
            lazy: async () => ({
              Component: path.includes("Report")
                ? (await import("./components/toc/ReportVersions")).ReportVersions
                : (await import("./components/hierarchypage/HierarchyHistoryTable"))
                    .HierarchyHistoryTable,
            }),
          },
          {
            path: "AdvancedSearch",
            lazy: async () => ({
              Component: (
                await import("./components/common/editorBlocksSearch/EditorBlocksSearchControls")
              ).EditorBlocksSearchControls,
            }),
          },
        ],
      })),

      // New TLF pages
      ...[
        "/Corporate",
        "/TA/:hierarchyTag",
        "/Project/:hierarchyTag",
        "/Study/:hierarchyTag",
        "/Study/:studyName/Report/:reportName",
      ].map((path) => ({
        path: `${path}/NewTlf/:newType`,
        loader: newTlfLoader,
        lazy: async () => ({
          Component: (await import("./pages/TlfPage")).TlfPage,
        }),
      })),

      // Old redirect links
      ...[
        "/H/:hierarchyId",
        "/H/:hierarchyId/:more",
        "/S/:hierarchyId",
        "/S/:hierarchyId/:more",
        "/R/:hierarchyId",
        "/R/:hierarchyId/:more",
      ].map((path) => ({
        path,
        loader: hierarchyRedirectLoader,
        element: <RedirectingPage />,
      })),

      // Old TlfDesigner pages and redirect from search
      ...["/TlfDesigner/:azsolId/:version", "/TlfDesigner/:id"].map((path) => ({
        path,
        loader: tlfRedirectLoader,
        element: <RedirectingPage />,
      })),

      // Load tlf
      ...[
        "/Corporate/Load",
        "/TA/:hierarchyTag/Load",
        "/Project/:hierarchyTag/Load",
        "/Study/:hierarchyTag/Load",
        "/Study/:studyName/Report/:reportName/Load",
        "/Study/:studyName/Report/:reportName/Version/:tocMajorVersion/Load",
      ].map((path) => ({
        path,
        loader: tlfLoader,
        lazy: async () => ({
          Component: (await import("./pages/TlfPage")).TlfPage,
        }),
      })),

      // Template pages
      ...["/Corporate", "/TA/:hierarchyTag", "/Project/:hierarchyTag"].map((path) => ({
        path,
        children: [
          {
            path: ":azsolId/:version",
            loader: tlfLoader,
            lazy: async () => ({
              Component: (await import("./pages/TlfPage")).TlfPage,
            }),
          },
        ],
      })),

      // Shell pages
      ...[
        "/Study/:studyName/Report/:reportName/:tocNumber",
        "/Study/:studyName/Report/:reportName/Version/:tocMajorVersion/:tocNumber",
      ].map((path) => ({
        path,
        loader: tlfLoader,
        lazy: async () => ({
          Component: (await import("./pages/TlfPage")).TlfPage,
        }),
      })),

      // Review pages
      ...["/Study/:studyName/Report/:reportName/Review"].map((path) => ({
        path,
        loader: hierarchyLoader,
        element: <TlfReview />,
        children: [
          { path: ":tocNumber", loader: tlfOnlyLoader, element: <TlfReviewRHS /> },
          {
            index: true,
            element: (
              <div className="w-100 h-100 d-flex align-items-center justify-content-center">
                Choose from the list of TLF items
              </div>
            ),
          },
        ],
      })),

      // Not found splat
      {
        path: "/*",
        lazy: async () => ({
          Component: (await import("./pages/NotFound")).NotFound,
        }),
      },
    ],
  },
];
